<template>
  <span v-if="isUrl" :class="getClass" :style="customStyle">
    <img :src="props.name" :alt="props.name" class="sb-icon-img" />
  </span>
  <span v-else v-html="getIcon(props.name)" :class="getClass" :style="customStyle" />
</template>
<script lang="ts" setup>
import { computed } from 'vue';
import get from 'lodash/get';
import * as icons from './../theme/icons';

const props = withDefaults(
  defineProps<{
    name: string;
    size?: 'xs' | 'sm' | 'base' | 'lg' | string | number;
  }>(),
  {
    size: 'base',
  }
);

const isUrl = computed(() => {
  try {
    return Boolean(new URL(props.name));
  } catch {
    return false;
  }
});

const getIcon = (name: string) => {
  const icon = get(icons, name, null);
  if (icon === null) {
    console.warn(`[IconComponent] - cannot find icon ${props.name}`);
  }
  return icon;
};

const customStyle = computed(() => {
  // Handle numeric sizes with custom styles
  if (typeof props.size === 'number') {
    return {
      width: `${props.size}px`,
      height: `${props.size}px`
    };
  }

  // Handle numeric strings
  const numericSize = parseInt(props.size);
  if (!isNaN(numericSize)) {
    return {
      width: `${numericSize}px`,
      height: `${numericSize}px`
    };
  }

  return {};
});

const getClass = computed(() => {
  // Handle numeric sizes for backward compatibility
  if (typeof props.size === 'number' || !isNaN(parseInt(props.size))) {
    return ['sb-icon', 'sb-icon-custom'];
  }

  // Map common numeric strings to size classes for known sizes
  const sizeMap: Record<string, string> = {
    '16': 'base',
    '20': 'sm',
    '24': 'base',
    '32': 'lg'
  };

  const mappedSize = sizeMap[props.size] || props.size;
  return ['sb-icon', `sb-icon-${mappedSize}`];
});
</script>

<style scoped>
.sb-icon-lg {
  @apply w-8 h-8;
}
.sb-icon-base {
  @apply w-6 h-6;
}
.sb-icon-sm {
  @apply w-5 h-5;
}
.sb-icon-xs {
  @apply w-4 h-4;
}
.sb-icon-custom {
  /* Size will be set via inline styles for custom numeric values */
  @apply block;
}
.sb-icon {
  @apply block;
}
.sb-icon :deep(svg) {
  @apply w-full h-full;
}
.sb-icon-img {
  @apply w-full h-full object-contain;
  display: block;
}
</style>
